import { DropdownAction } from '@/components/shared/action/Action';
import { FC, JSX, useEffect, useState } from 'react';
import { FiChevronDown, FiMapPin, FiUser, FiUsers } from 'react-icons/fi';
import { RiDatabase2Line } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../../components/page/PageContainer';
import { PageHeader } from '../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../components/page/PageSizer';
import { Button } from '../../../components/shared/button/Button';
import { Dropdown } from '../../../components/shared/dropdown/Dropdown';
import { TableContainer } from '../../../components/shared/table/TableContainer';
import { Permissions, useAuthenticationService } from '../../../services/authentication/authentication-service';
import { useFlag } from '../../../services/backstage/BackstageProvider';
import { handleError as handleUrqlError } from '../../../services/gql/config/URQLProvider';
import { useGetEmployeesByLocationsQuery } from '../../../services/gql/graphql.generated';
import { useLocationService } from '../../../services/location/LocationService';
import { UpdateSelectedLocationsModal } from '../../shifts/UpdateSelectedLocationsModal';
import strings from '../team.translations';
import { StaffMember, TeamTable } from './TeamTable';

export const TeamPage: FC = () => {
  const [permissions] = useAuthenticationService(state => [state.user?.profile.permission]);
  const canCreateEmployee = permissions?.includes(Permissions.EmployeeCreate) ?? false;
  const breadcrumbs = [{ name: strings.teamPageTitle, to: '/team' }];

  const addMultipleUsers = useFlag('addMultipleUsers');
  const importFlag = useFlag('import');
  const navigate = useNavigate();

  const addMultipleTeamMembers = () => {
    navigate('add-multiple');
  };

  const userFeatures: {
    key: string;
    featureEnabled?: boolean;
    label?: string;
    action?: () => void;
    icon?: JSX.Element;
  }[] = [
    {
      key: 'addSingleUser',
      featureEnabled: canCreateEmployee,
      label: strings.addTeamMenu.addATeamMember,
      action: () => navigate('/team/add-team-member'),
      icon: <FiUser className="w-4 h-4 mt-0.5 mr-2" />
    },
    {
      key: 'addMultipleUsers',
      featureEnabled: addMultipleUsers && canCreateEmployee,
      label: strings.addTeamMenu.addYourTeam,
      action: addMultipleTeamMembers,
      icon: <FiUsers className="w-4 h-4 mt-0.5 mr-2" />
    },
    {
      key: 'import',
      featureEnabled: importFlag,
      label: strings.addTeamMenu.importFromSpreadsheet,
      icon: <RiDatabase2Line className="w-4 h-4 mt-0.5 mr-2" />
    }
  ];
  const allowedUserFeatures = userFeatures.filter(feat => feat.featureEnabled);

  const [showUpdateLocationModal, setShowUpdateLocationModal] = useState<boolean>(false);

  const selectedLocation = useLocationService(state => state.selectedLocation);
  if (!selectedLocation) {
    throw Error('No location assigned.');
  }

  const [{ data, error, fetching }] = useGetEmployeesByLocationsQuery({
    variables: { locationIds: selectedLocation.id }
  });

  // Handle errors inside an effect so it doesn't interrupt the render cycle
  useEffect(() => {
    if (error) {
      handleUrqlError(error);
    }
  }, [error]);

  const staff = (data?.employeesByLocations?.employees || []) as StaffMember[];

  return (
    <PageContainer
      customHeader={
        <PageHeader pageTitle={strings.teamPageTitle} breadcrumbs={breadcrumbs} icon={<FiUser />}>
          <div className="flex flex-col md:flex-row gap-2 md:gap-5">
            {allowedUserFeatures.length > 1 ? (
              <Dropdown
                className="!m-0"
                button={
                  <Button primary className="w-full md:w-max mt-2 md:mt-0 flex justify-center items-center space-x-2">
                    <FiUsers />
                    <p>{strings.addTeamMenu.button}</p>
                    <FiChevronDown />
                  </Button>
                }
              >
                {allowedUserFeatures.map(feat => {
                  return (
                    <DropdownAction key={feat.key}>
                      {feat.icon}
                      <span onClick={feat.action}>{feat.label}</span>
                    </DropdownAction>
                  );
                })}
              </Dropdown>
            ) : (
              allowedUserFeatures.length > 0 &&
              allowedUserFeatures.map(feat => (
                <Button onClick={feat.action} key={feat.key} primary className="flex justify-center">
                  {feat.icon}
                  <span>{feat.label}</span>
                </Button>
              ))
            )}
          </div>
        </PageHeader>
      }
    >
      <PageSizer mode={SizerMode.PAGE}>
        <TableContainer>
          <TeamTable staff={staff} fetching={fetching} />
        </TableContainer>
      </PageSizer>
      <UpdateSelectedLocationsModal
        onCompleteCallback={() => {
          setShowUpdateLocationModal(false);
        }}
        open={showUpdateLocationModal}
        onClose={() => setShowUpdateLocationModal(false)}
      />
    </PageContainer>
  );
};
